import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useBills, useIncomes, usePayments, useTogglePayment } from "../lib/hooks";
import { formatMoney, monthRange, occurrencesInRange, todayIso } from "../lib/finance";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ChevronLeft, ChevronRight, CalendarDays, AlertTriangle, Download } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { usePaywall } from "../components/Paywall";

const STICKERS = ["💮", "✨", "🍀", "🎉", "🐷", "☕"];

export default function CalendarPage() {
  const { user } = useAuth();
  const currency = user?.prefs?.currency || "USD";
  const stickersOn = user?.prefs?.stickers_enabled ?? true;
  const { data: bills = [] } = useBills();
  const { data: incomes = [] } = useIncomes();
  const { data: payments = [] } = usePayments();
  const toggle = useTogglePayment();

  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selectedDay, setSelectedDay] = useState(null);
  const nav = useNavigate();
  const paywall = usePaywall();

  const { startIso, endIso, start, end } = monthRange(cursor.y, cursor.m);

  const paidSet = useMemo(
    () => new Set(payments.filter((p) => p.paid).map((p) => `${p.bill_id}_${p.occurrence_date}`)),
    [payments]
  );

  const dayMap = useMemo(() => {
    const m = {}; // date -> { incomes: [], bills: [] }
    for (const inc of incomes) {
      for (const d of occurrencesInRange(inc.first_date, inc.frequency, start, end)) {
        (m[d] ||= { incomes: [], bills: [] }).incomes.push(inc);
      }
    }
    for (const b of bills) {
      for (const d of occurrencesInRange(b.first_due_date, b.frequency, start, end)) {
        (m[d] ||= { incomes: [], bills: [] }).bills.push(b);
      }
    }
    return m;
  }, [bills, incomes, start, end]);

  // Running net across the month
  const runningNet = useMemo(() => {
    const dates = Object.keys(dayMap).sort();
    const map = {};
    let acc = 0;
    for (const d of dates) {
      const info = dayMap[d];
      const inflow = info.incomes.reduce((s, i) => s + Number(i.amount || 0), 0);
      const outflow = info.bills.reduce((s, b) => s + Number(b.amount || 0), 0);
      acc += inflow - outflow;
      map[d] = acc;
    }
    return map;
  }, [dayMap]);

  // Build calendar grid (Sun-start weeks)
  const grid = useMemo(() => {
    const first = new Date(start);
    const startDay = first.getDay(); // 0..6 (Sun=0)
    const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(cursor.y, cursor.m, d);
      cells.push(date.toISOString().slice(0, 10));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor, start]);

  const monthLabel = start.toLocaleString("default", { month: "long", year: "numeric" });
  const today = todayIso();

  const overdueCount = useMemo(() => {
    let n = 0;
    for (const b of bills) {
      const occs = occurrencesInRange(b.first_due_date, b.frequency, start, new Date(today));
      for (const d of occs) if (d < today && !paidSet.has(`${b.bill_id}_${d}`)) n++;
    }
    return n;
  }, [bills, paidSet, start, today]);

  const monthlyTotals = useMemo(() => {
    let income = 0, billTotal = 0, paid = 0, count = 0, paidCount = 0;
    for (const inc of incomes) {
      const occs = occurrencesInRange(inc.first_date, inc.frequency, start, end);
      income += occs.length * Number(inc.amount || 0);
    }
    for (const b of bills) {
      const occs = occurrencesInRange(b.first_due_date, b.frequency, start, end);
      count += occs.length;
      for (const d of occs) {
        billTotal += Number(b.amount || 0);
        if (paidSet.has(`${b.bill_id}_${d}`)) { paid += Number(b.amount || 0); paidCount++; }
      }
    }
    return { income, billTotal, paid, count, paidCount, net: income - billTotal };
  }, [bills, incomes, paidSet, start, end]);

  const nextMonth = () => {
    const target = new Date(cursor.y, cursor.m + 1, 1);
    const now = new Date(); const curThis = new Date(now.getFullYear(), now.getMonth(), 1);
    if (target.getTime() !== curThis.getTime() && !paywall.isPaid) { paywall.gateQuota("Historical & future months"); return; }
    setCursor({ y: target.getFullYear(), m: target.getMonth() });
  };
  const prevMonth = () => {
    const target = new Date(cursor.y, cursor.m - 1, 1);
    const now = new Date(); const curThis = new Date(now.getFullYear(), now.getMonth(), 1);
    if (target.getTime() !== curThis.getTime() && !paywall.isPaid) { paywall.gateQuota("Historical & future months"); return; }
    setCursor({ y: target.getFullYear(), m: target.getMonth() });
  };
  const goToday = () => { const d = new Date(); setCursor({ y: d.getFullYear(), m: d.getMonth() }); };

  const exportCSV = () => {
    const rows = [["Date", "Type", "Name", "Category", "Amount", "Status"]];
    for (const d of Object.keys(dayMap).sort()) {
      for (const inc of dayMap[d].incomes) rows.push([d, "Income", inc.name, "-", inc.amount, "Received"]);
      for (const b of dayMap[d].bills) {
        const isPaid = paidSet.has(`${b.bill_id}_${d}`);
        rows.push([d, "Bill", b.name, b.category, b.amount, isPaid ? "Paid" : "Unpaid"]);
      }
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `bill-system-${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };
  const exportPDF = () => {
    if (!paywall.gate("Newspaper PDF export")) return;
    nav(`/pdf?y=${cursor.y}&m=${cursor.m}`);
  };

  const selectedInfo = selectedDay ? dayMap[selectedDay] : null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="calendar-page">
      {paywall.Modal}
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth} data-testid="calendar-month-prev"><ChevronLeft size={16}/></Button>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight px-2" data-testid="calendar-month-label">{monthLabel}</h2>
          <Button variant="outline" size="icon" onClick={nextMonth} data-testid="calendar-month-next"><ChevronRight size={16}/></Button>
          <Button variant="ghost" onClick={goToday} data-testid="calendar-month-today"><CalendarDays size={14} className="mr-1"/>Today</Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn"><Download size={14} className="mr-1"/> CSV</Button>
          <Button variant="outline" onClick={exportPDF} data-testid="export-pdf-btn"><Download size={14} className="mr-1"/> PDF</Button>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryPill label="Income" value={formatMoney(monthlyTotals.income, currency)} color="text-[hsl(var(--paid-text))]" tid="summary-income"/>
        <SummaryPill label="Bills" value={formatMoney(monthlyTotals.billTotal, currency)} color="text-[hsl(var(--due-text))]" tid="summary-bills"/>
        <SummaryPill label="Ending net" value={formatMoney(monthlyTotals.net, currency)} color={monthlyTotals.net >= 0 ? "text-[hsl(var(--paid-text))]" : "text-[hsl(var(--overdue-text))]"} tid="summary-net"/>
        <SummaryPill label="Paid" value={`${monthlyTotals.paidCount}/${monthlyTotals.count}`} color="" tid="summary-paidcount"/>
      </div>

      {overdueCount > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--overdue-border))] bg-[hsl(var(--overdue-bg))] px-4 py-3 text-sm text-[hsl(var(--overdue-text))]" data-testid="overdue-banner">
          <AlertTriangle size={16}/> {overdueCount} overdue {overdueCount === 1 ? "bill" : "bills"} — tap the day to review.
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-2">
        <LegendChip color="bg-[hsl(var(--payday-border))]" label="Payday" tid="calendar-legend-payday"/>
        <LegendChip color="bg-[hsl(var(--paid-border))]" label="Paid bill" tid="calendar-legend-paid"/>
        <LegendChip color="bg-[hsl(var(--due-border))]" label="Due / Unpaid" tid="calendar-legend-due"/>
        <LegendChip color="bg-[hsl(var(--overdue-border))]" label="Overdue" tid="calendar-legend-overdue"/>
        <LegendChip color="bg-muted-foreground" label="Running net" tid="calendar-legend-net"/>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((w) => <div key={w} className="text-center">{w}</div>)}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-2 sm:gap-3">
        {grid.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[110px] rounded-xl bg-muted/20"/>;
          const info = dayMap[d] || { incomes: [], bills: [] };
          const hasEvent = info.incomes.length + info.bills.length > 0;
          const net = runningNet[d];
          const isToday = d === today;
          const paidHere = info.bills.filter((b) => paidSet.has(`${b.bill_id}_${d}`)).length;
          return (
            <button
              key={d}
              onClick={() => setSelectedDay(d)}
              data-testid={`calendar-day-cell-${d}`}
              className={`relative day-hover text-left min-h-[110px] rounded-xl border p-2 bg-card ${
                isToday ? "border-primary ring-2 ring-primary/30" : "border-border"
              }`}
            >
              <div className="flex items-baseline justify-between">
                <span className={`text-sm font-bold ${isToday ? "text-primary" : ""}`}>{parseInt(d.slice(-2), 10)}</span>
                {hasEvent && net !== undefined && (
                  <span
                    data-testid={`calendar-day-net-${d}`}
                    className={`tabular text-[10px] font-bold px-1.5 py-0.5 rounded-full ${net >= 0 ? "bg-[hsl(var(--paid-bg))] text-[hsl(var(--paid-text))]" : "bg-[hsl(var(--overdue-bg))] text-[hsl(var(--overdue-text))]"}`}
                  >
                    {net >= 0 ? "+" : ""}{formatMoney(net, currency)}
                  </span>
                )}
              </div>
              <div className="mt-1 space-y-1">
                {info.incomes.slice(0, 2).map((inc, idx) => (
                  <div key={`i-${idx}`} className="text-[10px] font-semibold truncate px-1.5 py-0.5 rounded"
                       style={{ background: "hsl(var(--payday-bg))", color: "hsl(var(--payday-text))", border: "1px solid hsl(var(--payday-border) / 0.4)" }}>
                    + {inc.name} · <span className="tabular">{formatMoney(inc.amount, currency)}</span>
                  </div>
                ))}
                {info.bills.slice(0, 2).map((b, idx) => {
                  const isPaid = paidSet.has(`${b.bill_id}_${d}`);
                  const isOverdue = !isPaid && d < today;
                  const bg = isPaid ? "hsl(var(--paid-bg))" : isOverdue ? "hsl(var(--overdue-bg))" : "hsl(var(--due-bg))";
                  const text = isPaid ? "hsl(var(--paid-text))" : isOverdue ? "hsl(var(--overdue-text))" : "hsl(var(--due-text))";
                  const border = isPaid ? "hsl(var(--paid-border))" : isOverdue ? "hsl(var(--overdue-border))" : "hsl(var(--due-border))";
                  return (
                    <div key={`b-${idx}`} className="text-[10px] font-semibold truncate px-1.5 py-0.5 rounded"
                         style={{ background: bg, color: text, border: `1px solid ${border} / 0.4` }}>
                      {isPaid ? "✓" : "•"} {b.name} · <span className="tabular">{formatMoney(b.amount, currency)}</span>
                    </div>
                  );
                })}
                {(info.bills.length + info.incomes.length) > 4 && (
                  <div className="text-[10px] text-muted-foreground">+{info.bills.length + info.incomes.length - 4} more</div>
                )}
              </div>
              {stickersOn && paidHere > 0 && (
                <span className="absolute -top-2 -right-2 text-xl sticker-pop select-none" aria-hidden>
                  {STICKERS[(parseInt(d.slice(-2), 10) + paidHere) % STICKERS.length]}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Day Inspector */}
      <Sheet open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <SheetContent data-testid="day-inspector">
          <SheetHeader>
            <SheetTitle>
              {selectedDay && new Date(selectedDay).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {selectedInfo?.incomes?.map((inc, idx) => (
              <div key={`di-${idx}`} className="rounded-lg border p-3"
                   style={{ borderColor: "hsl(var(--payday-border) / 0.6)", background: "hsl(var(--payday-bg))" }}>
                <div className="font-semibold flex items-center justify-between" style={{ color: "hsl(var(--payday-text))" }}>
                  <span>+ {inc.name} <span className="text-xs opacity-70">· {inc.category || "Income"}</span></span>
                  <span className="tabular">{formatMoney(inc.amount, currency)}</span>
                </div>
              </div>
            ))}
            {selectedInfo?.bills?.map((b, idx) => {
              const key = `${b.bill_id}_${selectedDay}`;
              const isPaid = paidSet.has(key);
              return (
                <div key={`db-${idx}`} className="rounded-lg border p-3 flex items-center justify-between"
                     data-testid={`day-inspector-bill-${b.bill_id}`}
                     style={{ borderColor: `hsl(var(--${isPaid ? "paid" : "due"}-border) / 0.6)`, background: `hsl(var(--${isPaid ? "paid" : "due"}-bg))` }}>
                  <div>
                    <div className="font-semibold" style={{ color: `hsl(var(--${isPaid ? "paid" : "due"}-text))` }}>{b.name}</div>
                    <div className="text-xs text-muted-foreground">{b.category} · <span className="tabular">{formatMoney(b.amount, currency)}</span></div>
                  </div>
                  <Button
                    size="sm"
                    variant={isPaid ? "secondary" : "default"}
                    onClick={() => toggle.mutate({ bill_id: b.bill_id, occurrence_date: selectedDay, paid: !isPaid })}
                    data-testid={`inspector-toggle-${b.bill_id}`}
                  >{isPaid ? "Undo" : "Mark paid"}</Button>
                </div>
              );
            })}
            {(!selectedInfo || (!selectedInfo.incomes?.length && !selectedInfo.bills?.length)) && (
              <div className="text-sm text-muted-foreground">No activity on this day. Take a walk 🌿</div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function SummaryPill({ label, value, color, tid }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3" data-testid={tid}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">{label}</div>
      <div className={`tabular text-lg sm:text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function LegendChip({ color, label, tid }) {
  return (
    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary text-xs font-semibold" data-testid={tid}>
      <span className={`w-2.5 h-2.5 rounded-full ${color}`}/> {label}
    </div>
  );
}
